/**
 * Backoffice CRM: the Ratings block of the recipe view (#2173).
 *
 * Lists a recipe's `recipes/{id}/feedback` documents, the anonymous ratings both apps' Rate
 * Recipe sheet writes (score, quick tags, chef's note, app version, device): a plain subcollection
 * read, newest first, sorted here so that a document without created_at still shows. The
 * Feedback page's Recipe ratings tab lists the same documents across every recipe.
 */

import { BADGE } from "./ui/classes.js";

/**
 * What a score means, in the words the apps show above the slider (FoodScoreReaction on iOS and
 * Android: the same bands, so the CRM never disagrees with the sheet about what an 8.5 feels like).
 */
export function foodScoreReaction(score) {
  if (typeof score !== "number" || isNaN(score)) return "";
  if (score < 2.5) return "😬 Call an ambulance (or order pizza)";
  if (score < 4.5) return "😐 Needs a little work. Actually, a lot of work.";
  if (score < 6.5) return "😋 Solid weeknight fuel. No complaints.";
  if (score < 8.5) return "🤩 Chef's kiss! Going into weekly rotation.";
  if (score < 10) return "😮‍💨 Michelin star material! Call Gordon Ramsay.";
  return "🤯 This is a solid 10 out of 10";
}

/** The score with its meaning on hover. */
export function scoreHtml(score, extraClass = "") {
  const reaction = foodScoreReaction(score);
  return `<span class="cursor-help ${extraClass}" title="${escapeHtml(reaction)}">${escapeHtml(formatScore(score))}<span class="font-normal text-gray-400 dark:text-gray-500"> / 10</span></span>`;
}

/** The recipe's ratings, newest first. */
export async function loadRecipeRatings({ db, collection, getDocs, recipeId }) {
  if (!recipeId || typeof collection !== "function" || typeof getDocs !== "function") return [];
  const snap = await getDocs(collection(db, "recipes", recipeId, "feedback"));
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  items.sort((a, b) => timeOf(b.created_at) - timeOf(a.created_at));
  return items;
}

/** How many, the mean score of those with a number, and how many came with a note. */
export function summarizeRatings(items) {
  const scored = items.filter((r) => typeof r.score === "number" && !isNaN(r.score));
  const average = scored.length ? scored.reduce((sum, r) => sum + r.score, 0) / scored.length : null;
  return { count: items.length, average, withNote: items.filter((r) => noteOf(r)).length };
}

/**
 * Fills the Ratings card: `undefined` while loading, `null` with an error when the read failed,
 * the list otherwise.
 */
export function renderRecipeRatings(items, error = null) {
  const list = document.getElementById("rvRatingsList");
  const summary = document.getElementById("rvRatingsSummary");
  const badge = document.getElementById("rvRatingsCountBadge");
  if (!list) return;

  if (items === undefined) {
    list.innerHTML = `<p class="py-6 text-center text-sm text-gray-500 dark:text-gray-400">Loading the ratings…</p>`;
    if (summary) summary.textContent = "";
    if (badge) badge.textContent = "…";
    return;
  }
  if (!Array.isArray(items)) {
    list.innerHTML = `<p class="py-6 text-center text-sm text-red-600 dark:text-red-400">Could not load the ratings: ${escapeHtml(error?.message || "unknown error")}</p>`;
    if (summary) summary.textContent = "";
    if (badge) badge.textContent = "?";
    return;
  }

  const { count, average, withNote } = summarizeRatings(items);
  if (badge) badge.textContent = String(count);
  if (summary) {
    summary.textContent = count === 0
      ? "Nobody has rated this recipe yet."
      : `${average === null ? "No scores" : `Average ${formatScore(average)} / 10`} · ${withNote} with a note`;
  }

  if (count === 0) {
    list.innerHTML = `<p class="py-6 text-center text-sm text-gray-500 dark:text-gray-400">No ratings yet. They arrive from the apps' Rate Recipe sheet, anonymous.</p>`;
    return;
  }

  list.innerHTML = `
    <div class="relative overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-300 dark:divide-white/15">
        <thead>
          <tr>
            <th scope="col" class="py-2 pr-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Rated</th>
            <th scope="col" class="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Score</th>
            <th scope="col" class="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Quick tags</th>
            <th scope="col" class="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Chef's note</th>
            <th scope="col" class="py-2 pl-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">App</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 dark:divide-white/10">
          ${items.map(ratingRowHtml).join("")}
        </tbody>
      </table>
    </div>`;
}

function ratingRowHtml(item) {
  const note = noteOf(item);
  const tags = (item.quick_tags || []).map((t) => `<span class="${BADGE.paprika}">${escapeHtml(String(t).replace(/_/g, " "))}</span>`).join("");
  const os = item.ios_version ? `iOS ${item.ios_version}` : (item.android_version ? `Android ${item.android_version}` : "");
  const app = [item.app_version ? `v${item.app_version}` : "", item.device_type || "", os].filter(Boolean).join(" · ");
  return `
          <tr class="align-top" data-rating-id="${escapeHtml(item.id)}">
            <td class="py-3 pr-3 text-sm whitespace-nowrap text-gray-900 dark:text-white">${escapeHtml(formatWhen(item.created_at))}${isHandled(item) ? `<div class="mt-1"><span class="${BADGE.green}">Handled</span></div>` : ""}</td>
            <td class="px-3 py-3 text-sm font-semibold whitespace-nowrap text-gray-900 dark:text-white">${scoreHtml(item.score)}</td>
            <td class="px-3 py-3"><div class="flex flex-wrap gap-1">${tags || `<span class="text-sm text-gray-400 dark:text-gray-500">–</span>`}</div></td>
            <td class="px-3 py-3 text-sm/6 break-words whitespace-pre-wrap text-gray-900 dark:text-gray-100">${note ? escapeHtml(note) : `<em class="text-gray-400 dark:text-gray-500">No note</em>`}</td>
            <td class="py-3 pl-3 text-xs whitespace-nowrap text-gray-500 dark:text-gray-400">${escapeHtml(app || "–")}</td>
          </tr>`;
}

function isHandled(item) {
  return item?.handled === true;
}

function noteOf(item) {
  const text = item?.feedback_note ?? "";
  return typeof text === "string" ? text.trim() : "";
}

function formatScore(score) {
  if (typeof score !== "number" || isNaN(score)) return "–";
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

function timeOf(ts) {
  if (!ts) return 0;
  if (ts.toMillis) return ts.toMillis();
  const t = new Date(ts).getTime();
  return isNaN(t) ? 0 : t;
}

function formatWhen(ts) {
  const t = timeOf(ts);
  if (!t) return "Unknown";
  return new Date(t).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function escapeHtml(str) {
  if (str === null || str === undefined) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
